import { useTranslation } from 'react-i18next';
import {
  AlertTriangle,
  ShieldCheck,
  HelpCircle,
  MapPin,
  ExternalLink,
} from 'lucide-react';

const OFFICIAL_SOURCES_NOTE =
  'Always follow your local emergency services and the National Weather Service for official alerts and evacuation orders.';

const EONET_SCOPE_NOTE =
  'Based on NASA tracking of large-scale natural events \u2014 this is not a full local weather forecast.';

const formatEventDate = (raw) => {
  if (!raw) return null;
  const ms = /^\d+$/.test(String(raw)) ? Number(raw) : Date.parse(raw);
  const d = new Date(ms);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const HazardEventCard = ({ event }) => {
  const { t } = useTranslation();
  const dateLabel = formatEventDate(event.date);

  return (
    <li className="list-none">
      <div className="bg-white rounded-xl border border-amber-200 p-4">
        <div className="flex items-start justify-between gap-2">
          <h4 className="font-semibold text-gray-900 text-sm">
            {event.title}
          </h4>
          {event.category ? (
            <span className="text-xs px-2 py-0.5 rounded-full flex-shrink-0 bg-amber-100 text-amber-800">
              {event.category}
            </span>
          ) : null}
        </div>

        {dateLabel ? (
          <div className="flex items-center gap-2 mt-2 text-xs text-gray-500">
            <span>{dateLabel}</span>
          </div>
        ) : null}

        {event.sourceUrl ? (
          <a
            href={event.sourceUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium text-amber-700 hover:text-amber-900 hover:underline focus:outline-none focus:ring-2 focus:ring-amber-400 rounded"
          >
            <ExternalLink className="w-3.5 h-3.5" aria-hidden="true" />
            {t('hazard_view_source', 'View source')}
          </a>
        ) : null}
      </div>
    </li>
  );
};

const HazardAlerts = ({ hazardStatus, events, onShareLocation }) => {
  const { t } = useTranslation();
  const list = Array.isArray(events) ? events : [];

  if (!hazardStatus || hazardStatus === 'none') return null;

  if (hazardStatus === 'no_location') {
    return (
      <section
        aria-label={t('hazard_alerts', 'Weather hazard alerts')}
        className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-4 sm:px-5 sm:py-5"
      >
        <div className="flex items-start gap-3">
          <div
            className="flex-shrink-0 w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center"
            aria-hidden="true"
          >
            <MapPin className="w-4 h-4 text-blue-700" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-semibold text-blue-900">
              {t('hazard_share_location_heading', 'Share your location to check for hazards')}
            </h3>
            <p className="mt-1 text-xs sm:text-sm text-blue-800/85">
              {t(
                'hazard_share_location_body',
                'We need your location to check for active weather hazards and natural disasters near you.'
              )}
            </p>
            {onShareLocation ? (
              <button
                type="button"
                onClick={onShareLocation}
                className="mt-3 inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 active:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors min-h-[40px] focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 focus:ring-offset-blue-50"
              >
                <MapPin className="w-4 h-4" aria-hidden="true" />
                {t('share_my_location', 'Share my location')}
              </button>
            ) : null}
          </div>
        </div>
      </section>
    );
  }

  if (hazardStatus === 'unverified') {
    return (
      <section
        aria-label={t('hazard_alerts', 'Weather hazard alerts')}
        className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-4 sm:px-5 sm:py-5"
      >
        <div className="flex items-start gap-3">
          <div
            className="flex-shrink-0 w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center"
            aria-hidden="true"
          >
            <HelpCircle className="w-4 h-4 text-amber-700" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-semibold text-amber-900">
              {t('hazard_unverified_heading', "We couldn't verify current hazards right now")}
            </h3>
            <p className="mt-1 text-xs sm:text-sm text-amber-800/90">
              {t(
                'hazard_unverified_body',
                'The hazard data source is temporarily unavailable, so we cannot confirm whether hazards are active near you. Do NOT assume all clear \u2014 check the National Weather Service, FEMA, or your local emergency management for official alerts.'
              )}
            </p>
          </div>
        </div>
      </section>
    );
  }

  if (hazardStatus === 'clear') {
    return (
      <section
        aria-label={t('hazard_alerts', 'Weather hazard alerts')}
        className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-4 sm:px-5 sm:py-5"
      >
        <div className="flex items-start gap-3">
          <div
            className="flex-shrink-0 w-9 h-9 rounded-full bg-emerald-100 flex items-center justify-center"
            aria-hidden="true"
          >
            <ShieldCheck className="w-4 h-4 text-emerald-700" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-semibold text-emerald-900">
              {t('hazard_clear_heading', 'No active large-scale hazards reported near you')}
            </h3>
            <p className="mt-1 text-xs sm:text-sm text-emerald-800/90">
              {t('hazard_scope_note', EONET_SCOPE_NOTE)}{' '}
              {t('hazard_official_sources', OFFICIAL_SOURCES_NOTE)}
            </p>
          </div>
        </div>
      </section>
    );
  }

  // active
  return (
    <section aria-label={t('hazard_alerts', 'Weather hazard alerts')}>
      <div className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-4 sm:px-5 sm:py-5">
        <div className="flex items-start gap-3">
          <div
            className="flex-shrink-0 w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center"
            aria-hidden="true"
          >
            <AlertTriangle className="w-4 h-4 text-amber-700" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-semibold text-amber-900">
              {t('hazard_active_heading', 'Active hazards near you')}
            </h3>
            <p className="mt-1 text-xs sm:text-sm text-amber-800/90">
              {t('hazard_scope_note', EONET_SCOPE_NOTE)}{' '}
              {t('hazard_official_sources', OFFICIAL_SOURCES_NOTE)}
            </p>
          </div>
        </div>

        {list.length > 0 ? (
          <ul className="space-y-3 mt-4">
            {list.map((event) => (
              <HazardEventCard key={event.id} event={event} />
            ))}
          </ul>
        ) : null}
      </div>
    </section>
  );
};

export default HazardAlerts;
